import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import 'swiper/css';
import { Swiper, SwiperSlide } from 'swiper/react';
import { leadingZero } from '../../utils/helpers';
import { Dialog } from '../dialog/base';
import './index.scss';

const DEBOUNCE_DELAY = 200; // 300ms debounce delay

type DialogTimePickerProps = {
  header?: string;
  open: boolean;
  setOpen: (value: boolean) => void;
  onSave: (data: string) => void;
  time: string;
  showHours?: boolean;
  showSeconds?: boolean;
  disableHours?: boolean;
  disableMinutes?: boolean;
  minHours?: number;
  maxHours?: number;
  minMinutes?: number;
  maxMinutes?: number;
  minSeconds?: number;
  maxSeconds?: number;
  onlyFullMinutes?: boolean;
};

export const DialogTimePicker: React.FC<DialogTimePickerProps> = ({
  header,
  open,
  setOpen,
  onSave,
  time,
  showSeconds,
  disableHours,
  disableMinutes,
  minHours = 0,
  maxHours = 23,
  minMinutes = 0,
  maxMinutes = 59,
  minSeconds = 0,
  maxSeconds = 59,
  onlyFullMinutes = false,
}) => {
  const { t } = useTranslation('common');
  const [hours, setHours] = useState({
    selected: leadingZero(0),
    range: [] as number[],
    slide: 0,
  });
  const [minutes, setMinutes] = useState({
    selected: leadingZero(0),
    range: [] as number[],
    slide: 0,
  });
  const [seconds, setSeconds] = useState({
    selected: leadingZero(0),
    range: [] as number[],
    slide: 0,
  });
  const [debounceTimeout, setDebounceTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);

  const generateRange = (min: number, max: number) => {
    return Array.from({ length: max - min + 1 }, (_, i) => min + i);
  };

  useEffect(() => {
    const parts = time.split(':');
    const h = +parts[0];
    const m = +parts[1];

    setHours(() => {
      const selected = leadingZero(h);
      const range = generateRange(minHours, maxHours);
      const slide = range.indexOf(+selected);
      return { selected, range, slide: slide >= 0 ? slide : 0 };
    });

    setMinutes(() => {
      const selected = leadingZero(m);
      const range = h > 0 ? generateRange(0, 59) : generateRange(minMinutes, maxMinutes);
      const slide = range.indexOf(+selected);
      return { selected, range, slide: slide >= 0 ? slide : 0 };
    });

    if (showSeconds && parts[2]) {
      const s = +parts[2];
      setSeconds(() => {
        const selected = leadingZero(s);
        const range = m > 0 ? generateRange(0, 59) : generateRange(minSeconds, maxSeconds);
        const slide = range.indexOf(+selected);
        return { selected, range, slide: slide >= 0 ? slide : 0 };
      });
    }
  }, [open, showSeconds, time, minHours, maxHours, minMinutes, maxMinutes, minSeconds, maxSeconds]);

  const handleDebouncedChange = useCallback(
    (handler: Function, ...args: any[]) => {
      if (debounceTimeout) clearTimeout(debounceTimeout);

      const timeout = setTimeout(() => {
        handler(...args);
      }, DEBOUNCE_DELAY);

      setDebounceTimeout(timeout);
    },
    [debounceTimeout],
  );

  const handleChangeHours = (e) => {
    const selected = hours.range[e.realIndex];
    const slideHours = hours.range.indexOf(selected);

    // Debounced change for minutes and seconds after hours are selected
    handleDebouncedChange(() => {
      if (selected > 0 || +minutes.selected > 0) {
        setMinutes((prev) => {
          const range = generateRange(0, 59);
          const slide = range.indexOf(+prev.selected);
          return {
            ...prev,
            range,
            slide: slide >= 0 ? slide : 0,
          };
        });
        setSeconds((prev) => {
          const range = generateRange(0, 59);
          const slide = range.indexOf(+prev.selected);
          return {
            ...prev,
            range,
            slide: slide >= 0 ? slide : 0,
          };
        });
      } else {
        setMinutes((prev) => {
          const range = generateRange(minMinutes, maxMinutes);
          const slide = range.indexOf(+prev.selected);
          return {
            selected: slide >= 0 ? prev.selected : leadingZero(minMinutes),
            range,
            slide: slide >= 0 ? slide : 0,
          };
        });
        setSeconds((prev) => {
          const range = generateRange(minSeconds, maxSeconds);
          const slide = range.indexOf(+prev.selected);
          return {
            selected: slide >= 0 ? prev.selected : leadingZero(minSeconds),
            range,
            slide: slide >= 0 ? slide : 0,
          };
        });
      }

      setHours({
        selected: leadingZero(selected),
        range: hours.range,
        slide: slideHours >= 0 ? slideHours : 0,
      });
    });
  };

  const handleChangeMinutes = (e) => {
    const selected = minutes.range[e.realIndex];
    const slideMin = minutes.range.indexOf(selected);

    handleDebouncedChange(() => {
      if (selected > 0 || +hours.selected > 0) {
        setSeconds((prev) => {
          const range = generateRange(0, 59);
          const slide = range.indexOf(+prev.selected);
          return {
            ...prev,
            range,
            slide: slide >= 0 ? slide : 0,
          };
        });
      } else {
        setSeconds((prev) => {
          const range = generateRange(minSeconds, maxSeconds);
          const slide = range.indexOf(+prev.selected);
          return {
            selected: slide >= 0 ? prev.selected : leadingZero(minSeconds),
            range,
            slide: slide >= 0 ? slide : 0,
          };
        });
      }

      setMinutes({
        selected: leadingZero(selected),
        range: minutes.range,
        slide: slideMin >= 0 ? slideMin : 0,
      });
    });
  };

  const handleChangeSeconds = (e) => {
    const selected = seconds.range[e.realIndex];
    const slide = seconds.range.indexOf(selected);

    handleDebouncedChange(() => {
      setSeconds({
        selected: leadingZero(selected),
        range: seconds.range,
        slide,
      });
    });
  };

  const onClose = () => setOpen(false);

  const onSubmit = () => {
    const result = [hours.selected, minutes.selected];
    if (showSeconds) result.push(seconds.selected);
    onSave(result.join(':'));
    onClose();
  };

  return (
    <Dialog setShow={setOpen} show={open} className="dialog--full-width">
      <div className="dialog-time-picker">
        <div className="dialog-time-picker__header">
          {header && <h3 className="dialog-time-picker__heading">{header}</h3>}
        </div>
        <div className="dialog-time-picker__time-wrapper">
          <div className="time-picker-wrapper">
            {!disableHours && (
              <>
                <div className="picker">
                  <Swiper
                    initialSlide={hours.slide}
                    slidesPerView={3}
                    direction="vertical"
                    grabCursor
                    centeredSlides
                    loop={hours.range.length > 3}
                    slideToClickedSlide
                    onRealIndexChange={handleChangeHours}
                  >
                    {hours.range.map((hour) => (
                      <SwiperSlide key={hour}>
                        <div className="time">{leadingZero(hour)}</div>
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </div>
                <div className="picker-divider">:</div>
              </>
            )}
            {!disableMinutes && (
              <>
                <div className="picker">
                  <Swiper
                    initialSlide={minutes.slide}
                    slidesPerView={3}
                    direction="vertical"
                    grabCursor
                    centeredSlides
                    loop={minutes.range.length > 3}
                    slideToClickedSlide
                    onRealIndexChange={handleChangeMinutes}
                  >
                    {[...(!onlyFullMinutes ? [...minutes.range] : [0])].map((minute) => (
                      <SwiperSlide key={minute}>
                        <div className="time">{leadingZero(minute)}</div>
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </div>
                {showSeconds && <div className="picker-divider">:</div>}
              </>
            )}
            {showSeconds && (
              <div className="picker">
                <Swiper
                  key={`${hours.selected}-${minutes.selected}`}
                  initialSlide={seconds.slide}
                  slidesPerView={3}
                  direction="vertical"
                  grabCursor
                  centeredSlides
                  loop={seconds.range.length > 3}
                  slideToClickedSlide
                  onRealIndexChange={handleChangeSeconds}
                >
                  {seconds.range.map((second) => (
                    <SwiperSlide key={second}>
                      <div className="time">{leadingZero(second)}</div>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>
            )}
          </div>
        </div>
        <div className="dialog-time-picker__buttons">
          <button className="dialog-time-picker__button" onClick={onClose}>
            {t('buttons.cancel')}
          </button>
          <button onClick={onSubmit} className="dialog-time-picker__button dialog-time-picker__button--active">
            {t('buttons.save')}
          </button>
        </div>
      </div>
    </Dialog>
  );
};
